/**
 * Page Certificats de Promotion KAIZEN
 *
 * Permet à Kru Ralph de :
 *  - Voir tous les certificats générés (filtre discipline + recherche nom)
 *  - Prévisualiser le PDF (inline)
 *  - Télécharger un certificat
 *  - Générer manuellement un nouveau certificat (sans évaluation)
 *  - Envoyer tous les certificats sélectionnés à son propre email (instructeur)
 *  - Supprimer
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Medal, Plus, Download, EnvelopeSimple, Trash, MagnifyingGlass, FilePdf, CheckCircle, DeviceMobile } from "@phosphor-icons/react";

const DISCIPLINES = [
  { value: "muay_thai", label: "Muay Thaï" },
  { value: "kickboxing", label: "Kickboxing" },
  { value: "jiu_jitsu_bresilien", label: "BJJ (Adultes)" },
  { value: "jujutsu_japonais", label: "Jiu-jitsu Can Ryu" },
];

const BELTS_BY_DISC = {
  muay_thai: ["Blanche", "Jaune", "Orange", "Rouge", "Vert", "Bleu", "Mauve", "Brune", "Noire"],
  kickboxing: ["Blanche", "Jaune", "Orange", "Rouge", "Vert", "Bleu", "Mauve", "Brune", "Noire"],
  jiu_jitsu_bresilien: ["Blanche", "Bleue", "Mauve", "Brune", "Noire"],
  jujutsu_japonais: ["Blanc-Jaune", "Jaune", "Orange", "Vert", "Bleu", "Mauve", "Brune", "Noire"],
};

// Paliers du programme Power Kicker Her (kickboxing au féminin)
const PK_NIVEAUX = ["Niveau Découverte", "Niveau Initiée", "Niveau Intermédiaire", "Niveau Avancée", "Niveau Élite"];

export default function CertificatsModule() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterDisc, setFilterDisc] = useState("");
  const [filterType, setFilterType] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showGenerate, setShowGenerate] = useState(false);
  const [previewCert, setPreviewCert] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/certificats");
      setCerts(r.data.certificats || []);
    } catch (e) {
      toast.error("Erreur chargement: " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }

  const filtered = useMemo(() => {
    return certs.filter((c) => {
      if (filterDisc && c.discipline !== filterDisc) return false;
      if (filterType === "promotion" && c.cert_type !== "promotion") return false;
      if (search && !c.student_name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [certs, search, filterDisc, filterType]);

  async function sendCertEmail(c) {
    const to = window.prompt(`Envoyer le certificat de ${c.student_name} par courriel à :`, "");
    if (to === null) return;
    if (!to.trim()) return toast.error("Courriel requis");
    toast.message("Envoi du courriel…");
    try {
      await api.post(`/certificats/${c.id}/send-email`, { to: to.trim() });
      toast.success(`Certificat envoyé à ${to.trim()}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("Gmail")) toast.error("Connecte d'abord ton Gmail — " + detail);
      else toast.error("Échec : " + detail);
    }
  }

  async function sendCertSms(c) {
    const to = window.prompt(`Envoyer le lien du certificat de ${c.student_name} par SMS au numéro :`, "");
    if (to === null) return;
    if (!to.trim()) return toast.error("Numéro requis");
    toast.message("Envoi du SMS…");
    try {
      await api.post(`/certificats/${c.id}/send-sms`, { to: to.trim() });
      toast.success(`SMS envoyé à ${to.trim()}`);
    } catch (e) {
      toast.error("Échec SMS : " + (e?.response?.data?.detail || e.message));
    }
  }

  function toggleSelect(id) {
    const n = new Set(selectedIds);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelectedIds(n);
  }

  function selectAll() {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((c) => c.id)));
    }
  }

  async function openPreview(cert) {
    setPreviewCert(cert);
    setPdfBlobUrl(null);
    try {
      const r = await api.get(`/certificats/${cert.id}/preview`, { responseType: "blob" });
      setPdfBlobUrl(URL.createObjectURL(r.data));
    } catch (e) {
      toast.error("Impossible de charger l'aperçu");
    }
  }

  function closePreview() {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPreviewCert(null);
  }

  async function downloadCert(cert) {
    try {
      const r = await api.get(`/certificats/${cert.id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (cert.student_name || "Certificat").replace(/[^\w\s-]/g, "_").replace(/\s+/g, "_");
      a.download = `Certificat_${safeName}_${cert.belt_obtained.replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement échoué");
    }
  }

  async function deleteCert(cert) {
    if (!confirm(`Supprimer le certificat de ${cert.student_name} ?`)) return;
    try {
      await api.delete(`/certificats/${cert.id}`);
      toast.success("Certificat supprimé");
      load();
    } catch (e) {
      toast.error("Suppression échouée");
    }
  }

  async function sendToInstructor() {
    if (selectedIds.size === 0) {
      toast.error("Sélectionne au moins un certificat");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/certificats/envoyer-instructeur", {
        cert_ids: Array.from(selectedIds),
      });
      toast.success(`✉️ ${r.data.cert_count} certificat(s) envoyé(s) à ${r.data.sent_to}`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error("Envoi échoué : " + (e?.response?.data?.detail || e.message));
    }
    setSending(false);
  }

  return (
    <div className="max-w-6xl mx-auto pb-12" data-testid="certificats-module">
      {/* Header */}
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-zinc-100 flex items-center gap-2">
            <Medal size={32} className="text-amber-400" weight="fill" />
            Certificats de Promotion
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            KAIZEN — premium dark+gold · à imprimer et remettre en main propre
          </p>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          data-testid="btn-new-cert"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus size={18} weight="bold" />
          Nouveau certificat
        </button>
      </div>

      {/* Stats + Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatPill label="Total" value={certs.length} color="zinc" />
          <StatPill label="Muay Thaï" value={certs.filter((c) => c.discipline === "muay_thai").length} color="amber" />
          <StatPill label="Kickboxing" value={certs.filter((c) => c.discipline === "kickboxing").length} color="amber" />
          <StatPill label="BJJ" value={certs.filter((c) => c.discipline === "jiu_jitsu_bresilien").length} color="amber" />
          <StatPill label="Can Ryu" value={certs.filter((c) => c.discipline === "jujutsu_japonais").length} color="amber" />
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Rechercher par nom…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-zinc-100 text-sm"
            />
          </div>
          <select
            value={filterDisc}
            onChange={(e) => setFilterDisc(e.target.value)}
            data-testid="filter-disc"
            className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm"
          >
            <option value="">Toutes disciplines</option>
            {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <div className="flex gap-1 bg-zinc-950 border border-zinc-700 rounded-lg p-1" data-testid="cert-type-tabs">
            <button onClick={() => setFilterType("")} data-testid="cert-tab-all"
              className={`px-3 py-1.5 rounded-md text-sm ${filterType === "" ? "bg-amber-600 text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
              Tous
            </button>
            <button onClick={() => setFilterType("promotion")} data-testid="cert-tab-promotions"
              className={`px-3 py-1.5 rounded-md text-sm ${filterType === "promotion" ? "bg-amber-600 text-black font-semibold" : "text-zinc-400 hover:text-zinc-200"}`}>
              🥋 Promotions ceinture
            </button>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      {selectedIds.size > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-amber-300 text-sm font-medium">
            {selectedIds.size} certificat(s) sélectionné(s)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-zinc-400 hover:text-zinc-200 px-3 py-1.5 text-sm"
            >
              Désélectionner
            </button>
            <button
              onClick={sendToInstructor}
              disabled={sending}
              data-testid="btn-send-instructor"
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold px-4 py-1.5 rounded-lg text-sm flex items-center gap-2"
            >
              <EnvelopeSimple size={16} weight="bold" />
              {sending ? "Envoi…" : "Envoyer à mon email"}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-zinc-500 text-center py-8">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-zinc-500 text-center py-12 bg-zinc-900 rounded-xl border border-zinc-800">
          <FilePdf size={48} className="mx-auto mb-3 text-zinc-700" />
          <p>Aucun certificat trouvé. Clique "Nouveau certificat" pour en créer un.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={selectAll}
            className="text-xs text-zinc-500 hover:text-amber-400 px-2 py-1 transition"
          >
            {selectedIds.size === filtered.length ? "Tout désélectionner" : "Tout sélectionner"}
          </button>
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`cert-row-${c.id}`}
              className={`flex items-center gap-3 bg-zinc-900 border rounded-xl p-3 transition ${
                selectedIds.has(c.id) ? "border-amber-500" : "border-zinc-800 hover:border-zinc-700"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(c.id)}
                onChange={() => toggleSelect(c.id)}
                className="w-4 h-4 accent-amber-500"
                data-testid={`select-${c.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-zinc-100 font-semibold truncate">{c.student_name}</span>
                  <span className="text-amber-400 text-xs bg-amber-500/10 px-2 py-0.5 rounded">{c.certificate_number}</span>
                  {c.sent_to_instructor && (
                    <span className="text-emerald-400 text-xs bg-emerald-500/10 px-2 py-0.5 rounded flex items-center gap-1">
                      <CheckCircle size={11} weight="fill" /> envoyé
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-400">
                  {c.discipline} · <span className="text-amber-400">{c.belt_obtained}</span> · {c.date_promotion}
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => openPreview(c)}
                  data-testid={`preview-${c.id}`}
                  title="Aperçu"
                  className="p-2 text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 rounded"
                >
                  <FilePdf size={18} />
                </button>
                <button
                  onClick={() => downloadCert(c)}
                  data-testid={`download-${c.id}`}
                  title="Télécharger"
                  className="p-2 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded"
                >
                  <Download size={18} />
                </button>
                <button
                  onClick={() => sendCertEmail(c)}
                  data-testid={`cert-email-${c.id}`}
                  title="Envoyer par courriel à l'élève"
                  className="p-2 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded"
                >
                  <EnvelopeSimple size={18} />
                </button>
                <button
                  onClick={() => sendCertSms(c)}
                  data-testid={`cert-sms-${c.id}`}
                  title="Envoyer par SMS à l'élève"
                  className="p-2 text-zinc-400 hover:text-purple-400 hover:bg-purple-500/10 rounded"
                >
                  <DeviceMobile size={18} />
                </button>
                <button
                  onClick={() => deleteCert(c)}
                  data-testid={`delete-${c.id}`}
                  title="Supprimer"
                  className="p-2 text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 rounded"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Generate */}
      {showGenerate && <GenerateModal onClose={() => setShowGenerate(false)} onDone={() => { setShowGenerate(false); load(); }} />}

      {/* Modal Preview */}
      {previewCert && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-zinc-100 font-semibold">{previewCert.student_name}</div>
                <div className="text-xs text-zinc-500">{previewCert.certificate_number} · {previewCert.belt_obtained}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => downloadCert(previewCert)} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 px-3 py-1.5 rounded text-sm font-semibold flex items-center gap-1.5">
                  <Download size={14} weight="bold" /> Télécharger
                </button>
                <button onClick={closePreview} className="text-zinc-400 hover:text-zinc-100 px-3 py-1.5">✕</button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-zinc-900">
              {pdfBlobUrl ? (
                <iframe src={pdfBlobUrl} className="w-full h-[75vh] rounded" title="Certificat preview" />
              ) : (
                <div className="text-zinc-500 text-center py-12">Chargement…</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatPill({ label, value, color = "zinc" }) {
  const cls = color === "amber" ? "text-amber-400 border-amber-500/30" : "text-zinc-300 border-zinc-700";
  return (
    <div className={`bg-zinc-950 border ${cls} rounded-lg p-3 text-center`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function GenerateModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    student_name: "",
    discipline: "jujutsu_japonais",
    belt_obtained: "Jaune",
    notes: "",
    cert_type: "promotion",
    secondary_coach_name: "",
  });
  const [saving, setSaving] = useState(false);
  const isPowerKick = form.cert_type === "powerkicker_her";
  const belts = isPowerKick ? PK_NIVEAUX : (BELTS_BY_DISC[form.discipline] || ["Blanche"]);

  function setCertType(t) {
    if (t === "powerkicker_her") {
      setForm((f) => ({
        ...f,
        cert_type: t,
        discipline: "kickboxing",
        belt_obtained: PK_NIVEAUX[0],
        secondary_coach_name: f.secondary_coach_name || "Coach Sonia",
      }));
    } else {
      setForm((f) => ({
        ...f,
        cert_type: t,
        belt_obtained: (BELTS_BY_DISC[f.discipline] || ["Blanche"])[0],
        secondary_coach_name: "",
      }));
    }
  }

  async function submit() {
    if (!form.student_name.trim()) {
      toast.error("Nom de l'élève requis");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/certificats/generer-manuel", form);
      toast.success(`📜 Certificat ${r.data.certificat.certificate_number} créé`);
      onDone();
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-amber-500/30 rounded-xl max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-amber-400 flex items-center gap-2">
          <Medal size={22} weight="fill" /> Nouveau certificat
        </h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Type de certificat</label>
            <select
              value={form.cert_type}
              onChange={(e) => setCertType(e.target.value)}
              data-testid="form-cert-type"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
            >
              <option value="promotion">Promotion (ceinture)</option>
              <option value="inscription">Certificat de bienvenue (nouveau membre)</option>
              <option value="powerkicker_her">Power Kicker Her (kickboxing au féminin)</option>
              <option value="powerkicker_session">Power Kicker Her — Complétion de session</option>
              <option value="powerkicker_anniversaire_1an">Power Kicker Her — 1 an</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Nom complet de l'élève</label>
            <input
              type="text"
              value={form.student_name}
              onChange={(e) => setForm({ ...form, student_name: e.target.value })}
              data-testid="form-student-name"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
              placeholder="ex: Sophie Tremblay"
            />
          </div>
          {!isPowerKick && (
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">Discipline</label>
              <select
                value={form.discipline}
                onChange={(e) => setForm({ ...form, discipline: e.target.value, belt_obtained: (BELTS_BY_DISC[e.target.value] || ["Blanche"])[0] })}
                data-testid="form-discipline"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
              >
                {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">{isPowerKick ? "Niveau / Palier" : "Ceinture obtenue"}</label>
            <select
              value={form.belt_obtained}
              onChange={(e) => setForm({ ...form, belt_obtained: e.target.value })}
              data-testid="form-belt"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
            >
              {belts.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          {form.cert_type.startsWith("powerkicker") && (
            <div>
              <label className="text-xs text-zinc-400 mb-1 block">2e signature (coach)</label>
              <input
                type="text"
                value={form.secondary_coach_name}
                onChange={(e) => setForm({ ...form, secondary_coach_name: e.target.value })}
                data-testid="form-secondary-coach"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100"
                placeholder="ex: Coach Sonia"
              />
              <div className="text-[10px] text-zinc-500 mt-0.5">Double signature : ce coach + Kru Ralph en chef.</div>
            </div>
          )}
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Note (optionnel)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="form-notes"
              rows={3}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm"
              placeholder="Message qui apparaît en italique au bas du certificat…"
              maxLength={140}
            />
            <div className="text-[10px] text-zinc-500 mt-0.5 text-right">{form.notes.length}/140</div>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-2 rounded-lg">Annuler</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="form-submit"
            className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-semibold py-2 rounded-lg"
          >
            {saving ? "Génération…" : "Générer le certificat"}
          </button>
        </div>
      </div>
    </div>
  );
}
